import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";
import { wooProduct } from "../../products/productInterface";
import { WooCommerce } from "../../../lib/api";
import React from "react";

const PRODUCTS_PER_PAGE = 100;

export const getWooProducts = async (
  page: number,
  categoryId?: string
): Promise<wooProduct[]> => {
  try {
    const params = new URLSearchParams({
      per_page: PRODUCTS_PER_PAGE.toString(),
      page: page.toString(),
    });

    const response = await WooCommerce.get(
      categoryId
        ? `products/?category=${categoryId}&${params}`
        : `products/?${params}`
    );
    const products = response.data;

    const filteredAndSortedProducts = products.sort(
      (a: wooProduct, b: wooProduct) =>
        a.name.localeCompare(b.name, undefined, {
          sensitivity: "base",
        })
    );

    return filteredAndSortedProducts;
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error;
  }
};

export function useProductData({
  categoryId,
}: {
  categoryId?: string;
}) {
  // Include categoryId and tagId in queryKey for proper cache invalidation
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ["woo-products", categoryId],
    queryFn: ({ pageParam = 1 }) =>
      getWooProducts(pageParam, categoryId),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === PRODUCTS_PER_PAGE
        ? allPages.length + 1
        : undefined,
  });

  // Move this logic into a useMemo to prevent unnecessary recalculations
  const allProducts = useMemo(
    () =>
      Array.from(
        new Map(
          data?.pages
            .flatMap((page) => page)
            .map((product) => [product.id, product])
        ).values()
      ),
    [data?.pages]
  );

  return {
    allProducts,
    isLoading,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
}

export function useFiltredProducts({
  categoryId,
}: {
  categoryId?: string;
}) {
  const [filteredProducts, setFilteredProducts] =
    React.useState<wooProduct[]>();

  const {
    allProducts,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useProductData({ categoryId });

  return {
    filteredProducts: filteredProducts || allProducts,
    allProducts,
    setFilteredProducts,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
}
